/**
 * fireConfetti — a short, dependency-free confetti burst (Sep 2026). Used when a
 * user first sees a Drivers award HR has released to them.
 *
 * A full-screen, pointer-events-none canvas on <body>, two cannons from the
 * bottom corners, the hub's chart colours, ~3 s, then it removes itself.
 * Skipped entirely under prefers-reduced-motion.
 */
export function fireConfetti({ count = 180, durationMs = 3200 }: { count?: number; durationMs?: number } = {}) {
  if (typeof window === 'undefined') return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

  const css = getComputedStyle(document.documentElement);
  const colors = ['--chart-2', '--chart-3', '--chart-4', '--chart-5', '--chart-6', '--brand-solid']
    .map((v) => css.getPropertyValue(v).trim().split(/\s+/).join(', '))
    .filter((v) => v.split(',').length === 3)
    .map((v) => `rgb(${v})`);
  if (colors.length === 0) colors.push('#8e3a8a', '#1d5fd1', '#0e9384', '#b54708');

  const canvas = document.createElement('canvas');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  Object.assign(canvas.style, {
    position: 'fixed', inset: '0', width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: '9999',
  } as Partial<CSSStyleDeclaration>);
  canvas.setAttribute('aria-hidden', 'true');
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) { canvas.remove(); return; }
  ctx.scale(dpr, dpr);

  const W = window.innerWidth, H = window.innerHeight;
  type P = { x: number; y: number; vx: number; vy: number; rot: number; vr: number; w: number; h: number; color: string; shape: 0 | 1 };
  const parts: P[] = Array.from({ length: count }, (_, i) => {
    const left = i % 2 === 0;
    const angle = (left ? -60 : -120) * (Math.PI / 180) + (Math.random() - 0.5) * 0.7;
    const speed = 11 + Math.random() * 9;
    return {
      x: left ? W * 0.05 : W * 0.95, y: H + 10,
      vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
      rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
      w: 6 + Math.random() * 6, h: 8 + Math.random() * 8,
      color: colors[Math.floor(Math.random() * colors.length)]!,
      shape: Math.random() < 0.3 ? 1 : 0,
    };
  });

  const start = performance.now();
  const tick = (now: number) => {
    const t = now - start;
    ctx.clearRect(0, 0, W, H);
    const fade = t > durationMs - 700 ? Math.max(0, (durationMs - t) / 700) : 1;
    for (const p of parts) {
      p.vy += 0.32; p.vx *= 0.99; p.vy *= 0.99;
      p.x += p.vx; p.y += p.vy; p.rot += p.vr;
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 1) { ctx.beginPath(); ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2); ctx.fill(); }
      else ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.rot * 2)) + 1);
      ctx.restore();
    }
    if (t < durationMs) requestAnimationFrame(tick);
    else canvas.remove();
  };
  requestAnimationFrame(tick);
}
